/**
 * `IPhoneFrame` — the dark device shell used by the Mobilversion module and the
 * Portal / Formulare / Meeting floating-phone overlays. Renders a metallic
 * frame, a black Dynamic-Island pill, an iOS status bar (9:41 + signal / wifi /
 * battery glyphs), and a bottom home-indicator bar. `children` render in the
 * inner screen. Styling is 1:1 with the design map §5.
 */
import { ld } from "@/lib/theme/light-dark";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "./hero-tokens";

/** iOS status bar: the 9:41 clock on the left, signal/wifi/battery on the right. */
function StatusBar(): ReactNode {
  const t = useTranslations("marketing.heroIphone");
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 13px",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <span
        style={{
          fontFamily: H.sans,
          fontSize: 15,
          fontWeight: 600,
          color: H.ink,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {t("k941")}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {/* Signal bars */}
        <svg width="17" height="12" viewBox="0 0 17 12" aria-hidden="true">
          <rect x="0" y="7.5" width="3" height="4.5" rx="1" fill={H.ink} />
          <rect x="4.67" y="5" width="3" height="7" rx="1" fill={H.ink} />
          <rect x="9.33" y="2.5" width="3" height="9.5" rx="1" fill={H.ink} />
          <rect x="14" y="0" width="3" height="12" rx="1" fill={H.ink} />
        </svg>
        {/* Wifi */}
        <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">
          <path
            d="M1.72 5.23 A8.2 8.2 0 0 1 14.28 5.23"
            fill="none"
            stroke={H.ink}
            strokeWidth="1.55"
            strokeLinecap="round"
          />
          <path
            d="M4.63 7.67 A4.4 4.4 0 0 1 11.37 7.67"
            fill="none"
            stroke={H.ink}
            strokeWidth="1.55"
            strokeLinecap="round"
          />
          <circle cx="8" cy="10" r="1" fill={H.ink} />
        </svg>
        {/* Battery */}
        <svg width="26" height="13" viewBox="0 0 26 13" aria-hidden="true">
          <rect
            x="0.6"
            y="0.6"
            width="22"
            height="11.8"
            rx="3"
            fill="none"
            stroke={H.ink}
            strokeOpacity="0.4"
            strokeWidth="1"
          />
          <rect x="2.4" y="2.4" width="16" height="8.2" rx="1.6" fill={H.ink} />
          <rect
            x="24"
            y="4.4"
            width="1.6"
            height="4.2"
            rx="0.8"
            fill={H.ink}
            fillOpacity="0.4"
          />
        </svg>
      </span>
    </div>
  );
}

/**
 * Dark iPhone shell. The inner screen (white by default) holds `children` and
 * is padded at the top so content clears the status bar.
 */
export function IPhoneFrame(props: {
  readonly children: ReactNode;
  readonly width?: number;
  readonly height?: number;
  readonly screenBg?: string;
  readonly style?: CSSProperties;
}): ReactNode {
  const width = props.width ?? 262;
  const height = props.height ?? 510;
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 44,
        padding: 5,
        background: "linear-gradient(150deg,#43464D 0%,#0C0E12 56%)",
        boxShadow:
          `0 50px 80px -34px ${ld("rgba(11,15,25,0.5)", "rgba(0,0,0,0.8)")}, 0 22px 40px -22px ${ld("rgba(11,15,25,0.34)", "rgba(0,0,0,0.56)")}, inset 0 2px 1.5px ${ld("rgba(255,255,255,0.18)", "rgba(255,255,255,0.06)")}, inset 0 0 0 1.5px rgba(255,255,255,0.06)`,
        flexShrink: 0,
        ...props.style,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 39,
          overflow: "hidden",
          background: props.screenBg ?? ld("#fff", "#181b1f"),
        }}
      >
        {/* Dynamic Island */}
        <span
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            width: 48,
            height: 21,
            borderRadius: 12,
            background: "#000",
            zIndex: 3,
          }}
        />
        <StatusBar />
        <div style={{ position: "absolute", inset: 0, paddingTop: 44 }}>
          {props.children}
        </div>
        {/* Home indicator */}
        <span
          style={{
            position: "absolute",
            bottom: 7,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            height: 5,
            borderRadius: 100,
            background: ld("rgba(11,15,25,0.85)", "rgba(250,250,250,0.85)"),
            zIndex: 3,
          }}
        />
      </div>
    </div>
  );
}
